// Chronicles read-aloud (Kokoro TTS). The server synthesizes the
// paragraph to WAV; this hook fetches it and plays it on the wall-TV
// (a Raspberry Pi 4 that can't run the model itself). One paragraph
// plays at a time; clicking a playing paragraph stops it.
import { useCallback, useRef, useState } from 'react';

const BASE = import.meta.env.VITE_API_URL ?? '';

export type TtsState = 'idle' | 'loading' | 'playing';

export function useChronicleTts() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [state, setState] = useState<TtsState>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setActiveId(null);
    setState('idle');
  }, []);

  const toggle = useCallback(async (id: string, text: string) => {
    // Toggling an already-playing paragraph stops it.
    if (activeId === id) {
      stop();
      return;
    }
    // Stop any current playback before starting another.
    stop();
    setActiveId(id);
    setState('loading');
    try {
      const res = await fetch(
        `${BASE}/api/public-board/chronicles/${encodeURIComponent(id)}/tts`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(`tts ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = stop;
      audio.onerror = stop;
      await audio.play();
      setState('playing');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('chronicle tts playback failed', err);
      stop();
    }
  }, [activeId, stop]);

  return { state, activeId, toggle, stop };
}
